import React, { createContext, useContext, useState, useEffect } from 'react';

// Comprehensive translations for entire site
const translations = {
  en: {
    // Navigation
    navHome: 'Home',
    navServices: 'Services',
    navWhoWeHelp: 'Who We Help',
    navHowItWorks: 'How It Works',
    navBlog: 'Blog',
    navAbout: 'About',
    navContact: 'Contact',
    navFaq: 'FAQ',
    navLogin: 'Client Login',
    navPortal: 'My Portal',
    navAdmin: 'Admin',
    navLogout: 'Logout',
    bookAppt: 'Book Appointment',
    getQuote: 'Get a Quote',
    languageLabel: 'EN',

    // Hero Section
    heroTitle: 'Maximize Your Tax Return',
    heroTitleHighlight: 'Nationwide Experts',
    heroSub: 'Fast, Accurate & Affordable tax preparation for individuals, businesses, and self-employed across all 50 states.',
    ctaBook: 'Book Free Consultation',
    ctaQuote: 'Get a Quote',

    // Trust Indicators
    irsAuthorized: 'IRS Authorized E-File Provider',
    allStates: 'All 50 States',
    since: 'Since 2021',
    secure: '100% Secure',
    bankLevelSecurity: 'Bank-Level Security',
    licensedSince: 'Licensed & Credentialed Since 2021',
    encryptedConfidential: 'Encrypted & Confidential',

    // Section Titles
    servicesTitle: 'Comprehensive Tax Solutions',
    servicesSub: 'Expert tax preparation for every situation — from simple W-2s to complex multi-state business filings.',
    whyTitle: 'Why Choose MaTaxPreparer?',
    whySub: 'Experience the difference of personalized, professional tax service.',
    processTitle: 'How It Works',
    processSub: 'Simple, transparent, and stress-free. Four easy steps to filed returns.',
    testimonialsTitle: 'What Our Clients Say',
    testimonialsSub: 'Real feedback from real clients we\'ve helped.',
    statsTitle: 'By the Numbers',
    blogTitle: 'Tax Tips & Insights',
    blogSub: 'Expert advice to help you make smart tax decisions.',
    contactTitle: 'Get in Touch',
    contactSub: 'We\'re here to help. Reach out and a tax professional will respond within 24 hours.',

    // Services
    serviceIndividual: 'Individual Tax Returns',
    serviceIndividualDesc: 'W-2, 1099, deductions, and credits — we maximize your personal refund.',
    serviceBusiness: 'Business Tax Returns',
    serviceBusinessDesc: 'LLC, S-Corp, C-Corp, and partnership returns filed accurately.',
    serviceSelfEmployed: 'Self-Employed / 1099',
    serviceSelfEmployedDesc: 'Freelancers and contractors — we find every deduction.',
    serviceMultiState: 'Multi-State Filing',
    serviceMultiStateDesc: 'Work or live in multiple states? We handle all jurisdictions.',
    servicePlanning: 'Tax Planning',
    servicePlanningDesc: 'Year-round strategy to minimize your tax burden legally.',
    serviceAudit: 'IRS Audit Help',
    serviceAuditDesc: 'Expert representation if you receive an IRS notice.',
    mostPopular: 'Most Popular',
    startingAt: 'Starting at',
    viewAllServices: 'View All Services',

    // Why Choose Us
    personalizedService: 'Personalized Service',
    personalizedServiceDesc: 'Unlike DIY software, you get a real tax professional reviewing every line.',
    yearRoundSupport: 'Year-Round Support',
    yearRoundSupportDesc: "We're available year-round, not just during tax season.",
    multiLayerReview: 'Multi-Layer Review',
    multiLayerReviewDesc: 'Every return is reviewed multiple times before filing for maximum accuracy.',
    nationwideCoverage: 'Nationwide Coverage',
    nationwideCoverageDesc: 'Licensed to prepare taxes in all 50 states. Online or in-person.',
    encryptedSecure: 'Encrypted & Secure',
    encryptedSecureDesc: 'Bank-level encryption protects all your financial data.',
    maximumRefund: 'Maximum Refund',
    maximumRefundDesc: 'We identify every deduction and credit you qualify for.',

    // Process Steps
    step1Title: 'Submit Documents',
    step1Desc: 'Upload your tax documents securely through our encrypted portal.',
    step2Title: 'Expert Review',
    step2Desc: 'Your dedicated tax professional reviews everything and prepares your return.',
    step3Title: 'Review & Approve',
    step3Desc: 'We walk you through the return and answer all your questions.',
    step4Title: 'File & Maximize',
    step4Desc: 'We e-file your return and ensure you get your maximum refund.',

    // Stats
    clientsServed: 'Clients Served',
    andGrowing: 'and growing',
    statesCovered: 'States Covered',
    nationwideService: 'nationwide service',
    yearsExperience: 'Years Experience',
    secureFiling: 'Secure Filing',
    irsAuthorizedShort: 'IRS authorized',

    // Footer
    footerTagline: 'Fast, Accurate & Affordable — Maximize Your Tax Return',
    footerQuickLinks: 'Quick Links',
    footerLegal: 'Legal',
    footerContact: 'Contact',
    footerPrivacy: 'Privacy Policy',
    footerTerms: 'Terms of Service',
    footerCircular230: 'Circular 230 Disclosure',
    footerAllRights: 'All rights reserved.',
    footerNationwide: 'Nationwide tax services in all 50 states. In-person or fully online.',
    footerBusinessHours: 'Mon–Fri: 9AM–5PM EST | Sat: 10AM–2PM',

    // Forms & Buttons
    submit: 'Submit',
    send: 'Send',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    continue: 'Continue',
    confirm: 'Confirm',
    loading: 'Loading...',
    processing: 'Processing...',
    sending: 'Sending...',
    uploading: 'Uploading...',
    success: 'Success!',
    error: 'Error',
    required: 'Required',
    optional: 'Optional',
    selectOption: 'Select an option...',
    noResults: 'No results found.',

    // Form Labels
    firstName: 'First Name',
    lastName: 'Last Name',
    fullName: 'Full Name',
    email: 'Email Address',
    phone: 'Phone',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    message: 'Message',
    subject: 'Subject',
    notes: 'Notes',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    category: 'Category',
    description: 'Description',
    amount: 'Amount',
    total: 'Total',
    serviceType: 'Service Type',
    serviceInterest: 'Service Interest',
    additionalNotes: 'Additional Notes',
    meetingType: 'Meeting Type',
    online: 'Online (Video/Phone)',
    inPerson: 'In-Person (Kennesaw, GA)',

    // Auth
    signIn: 'Sign In',
    signOut: 'Sign Out',
    signUp: 'Create Account',
    register: 'Register',
    login: 'Login',
    forgotPassword: 'Forgot Password?',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    createOneFree: 'Create one free',
    signInToPortal: 'Sign In to Portal',
    accessSecureDashboard: 'Access your secure client dashboard',
    welcomeBack: 'Welcome Back',
    joinOurPortal: 'Join Our Portal',
    startTaxJourney: 'Start your tax journey with MaTaxPreparer',
    passwordMin: 'Minimum 8 characters',
    authSecure: '256-bit encrypted · HTTPS · IRS Compliant',

    // Portal
    portalTitle: 'Client Portal',
    portalWelcome: 'Welcome back',
    securePortal: 'Secure Portal',
    dashboard: 'Dashboard',
    documents: 'Documents',
    appointments: 'Appointments',
    messages: 'Messages',
    invoices: 'Invoices',
    payments: 'Payments',
    profile: 'Profile',
    settings: 'Settings',
    quickActions: 'Quick Actions',
    uploadDocument: 'Upload Document',
    sendMessage: 'Send Message',
    viewInvoices: 'View Invoices',
    documentsUploaded: 'Documents Uploaded',
    pendingInvoices: 'Pending Invoices',
    notStarted: 'Not Started',
    inProgress: 'In Progress',
    completed: 'Completed',
    taxStatus: 'Tax Status',

    // Admin
    adminDashboard: 'Admin Dashboard',
    overview: 'Overview',
    leads: 'Leads',
    clients: 'Clients',
    quotes: 'Quotes',
    blogPosts: 'Blog Posts',
    analytics: 'Analytics',
    newLeads: 'New Leads',
    activeClients: 'Active Clients',
    pendingAppointments: 'Pending Appointments',
    revenue: 'Revenue',
    thisMonth: 'This Month',
    totalClients: 'Total Clients',
    clientManagement: 'Client Management',
    leadManagement: 'Lead Management',

    // Documents
    uploadFile: 'Upload File',
    uploadDocuments: 'Upload Documents',
    selectCategory: 'Select Category',
    fileSize: 'File must be under 5MB',
    supportedFormats: 'Supported: PDF, JPG, PNG, DOC, XLS',
    noDocuments: 'No documents uploaded yet.',
    documentCategories: {
      w2: 'W-2 Forms',
      '1099': '1099 Forms',
      expenses: 'Business Expenses',
      identity: 'Identity Documents',
      other: 'Other Documents'
    },

    // Appointments
    bookAppointment: 'Book Appointment',
    scheduleConsultation: 'Schedule a Consultation',
    freeConsultation: 'Free Consultation',
    selectServiceDate: 'Select Service & Date',
    selectTimeSlot: 'Select a Time Slot',
    yourDetails: 'Your Contact Details',
    availableTimes: 'Available times for',
    noSlotsAvailable: 'No available slots for this date.',
    chooseDifferentDate: 'Choose a different date',
    bookingSummary: 'Booking Summary',
    appointmentBooked: 'Appointment Booked!',
    appointmentScheduled: 'Your appointment has been scheduled for',
    confirmationSent: 'A confirmation has been sent to',
    preferToCall: 'Prefer to call?',

    // Contact
    letsTalk: "Let's Talk About Your Taxes",
    haveQuestions: "Have questions? We're here to help.",
    respondWithin24: 'We respond within 24 hours.',
    callUs: 'Call Us',
    emailUs: 'Email Us',
    visitUs: 'Visit Us',
    byAppointmentOnly: 'By appointment only',
    businessHours: 'Business Hours',
    mondayFriday: 'Monday – Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    closed: 'Closed',
    sendUsMessage: 'Send Us a Message',
    messageReceived: 'Message Received!',
    thankYouReaching: 'Thank you for reaching out. A tax professional will contact you within 24 hours.',
    sendAnother: 'Send Another Message',

    // FAQ
    faqTitle: 'Frequently Asked Questions',
    faqSub: 'Find answers to the most common questions about our tax services, pricing, and process.',
    stillHaveQuestions: 'Still Have Questions?',
    taxProfessionalsHelp: 'Our tax professionals are here to help. Book a free consultation or give us a call.',
    all: 'All',
    general: 'General',
    pricing: 'Pricing',
    selfEmployedCat: 'Self-Employed',
    business: 'Business',
    irsIssues: 'IRS Issues',
    security: 'Security',

    // Quote
    getCustomQuote: 'Get a Custom Quote',
    transparentPricing: 'Transparent pricing — no surprises. Tell us about your needs and we\'ll provide a firm quote.',
    startingRates: 'Starting Rates',
    finalPricingDepends: 'Final pricing depends on complexity. These are our starting rates:',
    whyOurPricing: 'Why Our Pricing?',
    noHiddenFees: 'No hidden fees — firm quote before we start',
    moreAffordable: 'More affordable than CPA firms',
    moreThorough: 'More thorough than DIY software',
    transparentInvoicing: 'Transparent line-item invoicing',
    requestYourQuote: 'Request Your Quote',
    tellUsNeeds: "Tell us about your needs and we'll respond within 24 hours.",
    tellUsMore: 'Tell Us More',
    describeYourSituation: 'Describe your tax situation, number of states, business type, special circumstances...',
    requestQuote: 'Request Quote',
    quoteReceived: 'Quote Request Received!',
    prepareCustomQuote: "We'll prepare a custom quote and reach out within 24 business hours.",

    // Legal
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    circular230Disclosure: 'IRS Circular 230 Disclosure',
    effectiveDate: 'Effective Date',
    requiredByIRS: 'Required by IRS Regulations',
    governingLaw: 'Governing Law: State of Georgia',

    // About
    meetFounder: 'Meet MaTaxPreparer LLC',
    ourFounder: 'Our Founder',
    irsLicensedPreparer: 'IRS Licensed Tax Preparer',
    ourValues: 'Our Values',
    whatWeStandFor: 'What We Stand For',
    accuracyFirst: 'Accuracy First',
    accuracyFirstDesc: 'Every return undergoes multiple reviews before filing. We never rush — we get it right.',
    confidentiality: 'Confidentiality',
    confidentialityDesc: 'Your financial information is encrypted and never shared without your explicit consent.',
    accessibility: 'Accessibility',
    accessibilityDesc: "Premium tax expertise shouldn't be out of reach. We keep our pricing transparent and fair.",
    yearRoundPartner: 'Year-Round Partner',
    yearRoundPartnerDesc: 'Tax planning is a 12-month conversation, not a once-a-year event.',
    findUs: 'Find Us',
    readyToWork: 'Ready to Work With Us?',
    discoverMaximize: 'Book your free consultation today and discover how we can maximize your return.',

    // Who We Help
    taxHelpEveryone: 'Tax Help for Everyone',
    fromSimpleW2: 'From simple W-2 returns to complex multi-state business filings — we have the expertise to handle your unique tax situation.',
    notSureWhereFit: 'Not Sure Where You Fit?',
    assessYourSituation: "Book a free consultation and we'll assess your situation and recommend the best approach.",
    individualsAndFamilies: 'Individuals & Families',
    w2Employees: 'W-2 employees, retirees, students',
    selfEmployedFreelancers: 'Self-Employed & Freelancers',
    contractors1099: '1099 contractors, gig workers, consultants',
    smallMediumBusinesses: 'Small & Medium Businesses',
    llcScorpCcorp: 'LLCs, S-Corps, C-Corps, partnerships',
    multiStateFilers: 'Multi-State Filers',
    remoteWorkers: 'Remote workers, travelers, dual residents',
    startupsNewBusinesses: 'Startups & New Businesses',
    earlyStageCompanies: 'Early-stage companies, founders',

    // How It Works
    theProcess: 'The Process',
    simpleTransparent: 'Simple, transparent, and stress-free. From document submission to filed return in four easy steps.',
    submitYourDocuments: 'Submit Your Documents',
    secureUploadMinutes: 'Secure upload in minutes',
    createFreeAccount: 'Create your free secure account',
    uploadViEncrypted: 'Upload documents via encrypted portal',
    followGuidedChecklist: 'Follow our guided document checklist',
    addNotesQuestions: 'Add notes or questions for your advisor',
    realTimeConfirmation: 'Real-time upload confirmation',
    expertReviewPrep: 'Expert Review & Preparation',
    dedicatedProfessional: 'Your dedicated tax professional at work',
    dedicatedAssigned: 'Dedicated tax professional assigned',
    lineByLineReview: 'Line-by-line document review',
    maxDeductionsIdentified: 'Maximum deductions identified',
    taxPlanningRecommendations: 'Tax planning recommendations',
    questionsAddressed: 'Questions addressed proactively',
    reviewApprove: 'Review & Approve',
    alwaysInControl: "You're always in control",
    clearExplanation: 'Clear explanation of your return',
    lineByLineWalkthrough: 'Line-by-line walkthrough available',
    allQuestionsAnswered: 'All questions answered',
    digitalSignature: 'Digital signature process',
    approvalConvenience: 'Approval at your convenience',
    fileMaximize: 'File & Maximize',
    secureEfiling: 'Secure e-filing and refund tracking',
    irsAuthorizedEfile: 'IRS-authorized e-filing',
    stateFilingAll50: 'State filing in all 50 states',
    instantConfirmation: 'Instant filing confirmation',
    refundTracking: 'Refund status tracking',
    postFilingSupport: 'Post-filing support',
    fastTurnaround: 'Fast Turnaround',
    businessDays35: '3-5 business days',
    beforeEveryFiling: 'Before every filing',
    federalState: 'Federal + State',
    readyGetStarted: 'Ready to Get Started?',
    walkThroughProcess: "Book your free consultation and we'll walk you through the entire process.",

    // Blog
    taxInsights: 'Tax Insights',
    theBlog: 'The MaTaxPreparer Blog',
    expertInsights: 'Expert insights on taxes, deductions, and financial strategy — written by our tax professionals.',
    searchArticles: 'Search articles...',
    loadingArticles: 'Loading articles...',
    noArticlesFound: 'No articles found.',
    backToBlog: 'Back to Blog',
    haveTaxQuestions: 'Have tax questions?',
    teamHereToHelp: 'Our team at MaTaxPreparer LLC is here to help. Book a free consultation today.',

    // AI Chatbot
    aiChatbotName: 'Alex — Tax Advisor AI',
    aiChatbotGreeting: "Hi! I'm Alex, your AI tax advisor from MaTaxPreparer LLC. How can I help you today? You can ask me about tax deductions, filing deadlines, business taxes, and more!",
    aiChatbotError: "I'm having trouble connecting right now. Please call us at 770-235-6999 or email mataxpreparerllc@yahoo.com for immediate help.",
    quickQuestions: 'Quick Questions',
    whatDocumentsNeed: 'What documents do I need for my taxes?',
    canDeductHomeOffice: 'Can I deduct home office expenses?',
    whenFilingDeadline: 'When is the tax filing deadline?',
    typeYourQuestion: 'Type your tax question...',

    // Errors
    invalidEmail: 'Please enter a valid email address.',
    invalidPassword: 'Password must be at least 8 characters.',
    invalidPhone: 'Please enter a valid phone number.',
    fieldRequired: 'This field is required.',
    somethingWentWrong: 'Something went wrong. Please try again.',
    emailAlreadyRegistered: 'This email is already registered.',
    invalidCredentials: 'Invalid email or password.',
    sessionExpired: 'Your session has expired. Please log in again.',
    networkError: 'Network error. Please check your connection.',
    fileTooBig: 'File must be under 5MB.',
    unsupportedFileType: 'This file type is not supported.',
  },

  es: {
    // Navigation
    navHome: 'Inicio',
    navServices: 'Servicios',
    navWhoWeHelp: 'A Quién Ayudamos',
    navHowItWorks: 'Cómo Funciona',
    navBlog: 'Blog',
    navAbout: 'Nosotros',
    navContact: 'Contacto',
    navFaq: 'Preguntas',
    navLogin: 'Portal de Clientes',
    navPortal: 'Mi Portal',
    navAdmin: 'Admin',
    navLogout: 'Cerrar Sesión',
    bookAppt: 'Reservar Cita',
    getQuote: 'Cotización',
    languageLabel: 'ES',

    // Hero Section
    heroTitle: 'Maximice Su Declaración de Impuestos',
    heroTitleHighlight: 'Expertos Nacionales',
    heroSub: 'Preparación de impuestos rápida, precisa y asequible para individuos, negocios y autónomos en los 50 estados.',
    ctaBook: 'Reservar Consulta Gratis',
    ctaQuote: 'Obtener Cotización',

    // Trust Indicators
    irsAuthorized: 'Proveedor E-File Autorizado por el IRS',
    allStates: 'Los 50 Estados',
    since: 'Desde 2021',
    secure: '100% Seguro',
    bankLevelSecurity: 'Seguridad Bancaria',
    licensedSince: 'Licenciado y Acreditado Desde 2021',
    encryptedConfidential: 'Encriptado y Confidencial',

    // Section Titles
    servicesTitle: 'Soluciones Fiscales Integrales',
    servicesSub: 'Preparación de impuestos experta para cada situación — desde simples W-2 hasta declaraciones comerciales complejas.',
    whyTitle: '¿Por Qué MaTaxPreparer?',
    whySub: 'Experimente la diferencia del servicio fiscal profesional y personalizado.',
    processTitle: 'Cómo Funciona',
    processSub: 'Simple, transparente y sin estrés. Cuatro pasos fáciles para presentar sus declaraciones.',
    testimonialsTitle: 'Lo Que Dicen Nuestros Clientes',
    testimonialsSub: 'Comentarios reales de clientes reales que hemos ayudado.',
    statsTitle: 'En Números',
    blogTitle: 'Consejos e Ideas Fiscales',
    blogSub: 'Asesoramiento experto para ayudarle a tomar decisiones fiscales inteligentes.',
    contactTitle: 'Contáctenos',
    contactSub: 'Estamos aquí para ayudar. Comuníquese y un profesional de impuestos le responderá en 24 horas.',

    // Services
    serviceIndividual: 'Declaraciones de Impuestos Individuales',
    serviceIndividualDesc: 'W-2, 1099, deducciones y créditos — maximizamos su reembolso personal.',
    serviceBusiness: 'Declaraciones de Impuestos Comerciales',
    serviceBusinessDesc: 'LLC, S-Corp, C-Corp y declaraciones de sociedad presentadas con precisión.',
    serviceSelfEmployed: 'Autónomo / 1099',
    serviceSelfEmployedDesc: 'Freelancers y contratistas — encontramos cada deducción.',
    serviceMultiState: 'Declaración Multi-Estado',
    serviceMultiStateDesc: '¿Trabaja o vive en múltiples estados? Manejamos todas las jurisdicciones.',
    servicePlanning: 'Planificación Fiscal',
    servicePlanningDesc: 'Estrategia durante todo el año para minimizar su carga fiscal legalmente.',
    serviceAudit: 'Ayuda con Auditorías del IRS',
    serviceAuditDesc: 'Representación experta si recibe un aviso del IRS.',
    mostPopular: 'Más Popular',
    startingAt: 'Desde',
    viewAllServices: 'Ver Todos los Servicios',

    // Why Choose Us
    personalizedService: 'Servicio Personalizado',
    personalizedServiceDesc: 'A diferencia del software DIY, obtiene un profesional de impuestos real revisando cada línea.',
    yearRoundSupport: 'Soporte Todo el Año',
    yearRoundSupportDesc: 'Estamos disponibles todo el año, no solo durante la temporada de impuestos.',
    multiLayerReview: 'Revisión Múltiple',
    multiLayerReviewDesc: 'Cada declaración se revisa múltiples veces antes de presentar para máxima precisión.',
    nationwideCoverage: 'Cobertura Nacional',
    nationwideCoverageDesc: 'Licenciados para preparar impuestos en los 50 estados. En línea o en persona.',
    encryptedSecure: 'Encriptado y Seguro',
    encryptedSecureDesc: 'El cifrado de nivel bancario protege todos sus datos financieros.',
    maximumRefund: 'Máximo Reembolso',
    maximumRefundDesc: 'Identificamos cada deducción y crédito para el que califica.',

    // Process Steps
    step1Title: 'Enviar Documentos',
    step1Desc: 'Suba sus documentos fiscales de forma segura a través de nuestro portal encriptado.',
    step2Title: 'Revisión Experta',
    step2Desc: 'Su profesional de impuestos dedicado revisa todo y prepara su declaración.',
    step3Title: 'Revisar y Aprobar',
    step3Desc: 'Le explicamos la declaración y respondemos todas sus preguntas.',
    step4Title: 'Presentar y Maximizar',
    step4Desc: 'Presentamos electrónicamente su declaración y aseguramos su máximo reembolso.',

    // Stats
    clientsServed: 'Clientes Atendidos',
    andGrowing: 'y creciendo',
    statesCovered: 'Estados Cubiertos',
    nationwideService: 'servicio nacional',
    yearsExperience: 'Años de Experiencia',
    secureFiling: 'Presentación Segura',
    irsAuthorizedShort: 'autorizado por IRS',

    // Footer
    footerTagline: 'Rápido, Preciso y Asequible — Maximice Su Reembolso',
    footerQuickLinks: 'Enlaces Rápidos',
    footerLegal: 'Legal',
    footerContact: 'Contacto',
    footerPrivacy: 'Política de Privacidad',
    footerTerms: 'Términos de Servicio',
    footerCircular230: 'Divulgación Circular 230',
    footerAllRights: 'Todos los derechos reservados.',
    footerNationwide: 'Servicios fiscales nacionales en los 50 estados. En persona o completamente en línea.',
    footerBusinessHours: 'Lun–Vie: 9AM–5PM EST | Sáb: 10AM–2PM',

    // Forms & Buttons
    submit: 'Enviar',
    send: 'Enviar',
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Eliminar',
    edit: 'Editar',
    close: 'Cerrar',
    back: 'Atrás',
    next: 'Siguiente',
    continue: 'Continuar',
    confirm: 'Confirmar',
    loading: 'Cargando...',
    processing: 'Procesando...',
    sending: 'Enviando...',
    uploading: 'Subiendo...',
    success: '¡Éxito!',
    error: 'Error',
    required: 'Requerido',
    optional: 'Opcional',
    selectOption: 'Seleccione una opción...',
    noResults: 'No se encontraron resultados.',

    // Form Labels
    firstName: 'Nombre',
    lastName: 'Apellido',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    phone: 'Teléfono',
    password: 'Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    message: 'Mensaje',
    subject: 'Asunto',
    notes: 'Notas',
    date: 'Fecha',
    time: 'Hora',
    status: 'Estado',
    category: 'Categoría',
    description: 'Descripción',
    amount: 'Cantidad',
    total: 'Total',
    serviceType: 'Tipo de Servicio',
    serviceInterest: 'Servicio de Interés',
    additionalNotes: 'Notas Adicionales',
    meetingType: 'Tipo de Reunión',
    online: 'En Línea (Video/Teléfono)',
    inPerson: 'En Persona (Kennesaw, GA)',

    // Auth
    signIn: 'Iniciar Sesión',
    signOut: 'Cerrar Sesión',
    signUp: 'Crear Cuenta',
    register: 'Registrarse',
    login: 'Iniciar Sesión',
    forgotPassword: '¿Olvidó su Contraseña?',
    createAccount: 'Crear Cuenta',
    alreadyHaveAccount: '¿Ya tiene una cuenta?',
    dontHaveAccount: '¿No tiene una cuenta?',
    createOneFree: 'Cree una gratis',
    signInToPortal: 'Iniciar Sesión en el Portal',
    accessSecureDashboard: 'Acceda a su panel de cliente seguro',
    welcomeBack: 'Bienvenido de Nuevo',
    joinOurPortal: 'Únase a Nuestro Portal',
    startTaxJourney: 'Comience su viaje fiscal con MaTaxPreparer',
    passwordMin: 'Mínimo 8 caracteres',
    authSecure: 'Encriptado 256-bit · HTTPS · Cumple con IRS',

    // Portal
    portalTitle: 'Portal de Clientes',
    portalWelcome: 'Bienvenido de nuevo',
    securePortal: 'Portal Seguro',
    dashboard: 'Panel',
    documents: 'Documentos',
    appointments: 'Citas',
    messages: 'Mensajes',
    invoices: 'Facturas',
    payments: 'Pagos',
    profile: 'Perfil',
    settings: 'Configuración',
    quickActions: 'Acciones Rápidas',
    uploadDocument: 'Subir Documento',
    sendMessage: 'Enviar Mensaje',
    viewInvoices: 'Ver Facturas',
    documentsUploaded: 'Documentos Subidos',
    pendingInvoices: 'Facturas Pendientes',
    notStarted: 'No Iniciado',
    inProgress: 'En Progreso',
    completed: 'Completado',
    taxStatus: 'Estado Fiscal',

    // Admin
    adminDashboard: 'Panel de Administración',
    overview: 'Resumen',
    leads: 'Prospectos',
    clients: 'Clientes',
    quotes: 'Cotizaciones',
    blogPosts: 'Publicaciones del Blog',
    analytics: 'Análisis',
    newLeads: 'Nuevos Prospectos',
    activeClients: 'Clientes Activos',
    pendingAppointments: 'Citas Pendientes',
    revenue: 'Ingresos',
    thisMonth: 'Este Mes',
    totalClients: 'Total de Clientes',
    clientManagement: 'Gestión de Clientes',
    leadManagement: 'Gestión de Prospectos',

    // Documents
    uploadFile: 'Subir Archivo',
    uploadDocuments: 'Subir Documentos',
    selectCategory: 'Seleccionar Categoría',
    fileSize: 'El archivo debe ser menor a 5MB',
    supportedFormats: 'Soportados: PDF, JPG, PNG, DOC, XLS',
    noDocuments: 'No hay documentos subidos todavía.',
    documentCategories: {
      w2: 'Formularios W-2',
      '1099': 'Formularios 1099',
      expenses: 'Gastos Comerciales',
      identity: 'Documentos de Identidad',
      other: 'Otros Documentos'
    },

    // Appointments
    bookAppointment: 'Reservar Cita',
    scheduleConsultation: 'Programar una Consulta',
    freeConsultation: 'Consulta Gratuita',
    selectServiceDate: 'Seleccionar Servicio y Fecha',
    selectTimeSlot: 'Seleccionar Horario',
    yourDetails: 'Sus Datos de Contacto',
    availableTimes: 'Horarios disponibles para',
    noSlotsAvailable: 'No hay horarios disponibles para esta fecha.',
    chooseDifferentDate: 'Elegir una fecha diferente',
    bookingSummary: 'Resumen de Reserva',
    appointmentBooked: '¡Cita Reservada!',
    appointmentScheduled: 'Su cita ha sido programada para',
    confirmationSent: 'Se ha enviado una confirmación a',
    preferToCall: '¿Prefiere llamar?',

    // Contact
    letsTalk: 'Hablemos de Sus Impuestos',
    haveQuestions: '¿Tiene preguntas? Estamos aquí para ayudar.',
    respondWithin24: 'Respondemos en 24 horas.',
    callUs: 'Llámenos',
    emailUs: 'Escríbanos',
    visitUs: 'Visítenos',
    byAppointmentOnly: 'Solo con cita',
    businessHours: 'Horario de Atención',
    mondayFriday: 'Lunes – Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
    closed: 'Cerrado',
    sendUsMessage: 'Envíenos un Mensaje',
    messageReceived: '¡Mensaje Recibido!',
    thankYouReaching: 'Gracias por comunicarse. Un profesional de impuestos le contactará en 24 horas.',
    sendAnother: 'Enviar Otro Mensaje',

    // FAQ
    faqTitle: 'Preguntas Frecuentes',
    faqSub: 'Encuentre respuestas a las preguntas más comunes sobre nuestros servicios fiscales, precios y proceso.',
    stillHaveQuestions: '¿Todavía Tiene Preguntas?',
    taxProfessionalsHelp: 'Nuestros profesionales de impuestos están aquí para ayudar. Reserve una consulta gratuita o llámenos.',
    all: 'Todas',
    general: 'General',
    pricing: 'Precios',
    selfEmployedCat: 'Autónomos',
    business: 'Negocios',
    irsIssues: 'Problemas con IRS',
    security: 'Seguridad',

    // Quote
    getCustomQuote: 'Obtener Cotización Personalizada',
    transparentPricing: 'Precios transparentes — sin sorpresas. Cuéntenos sus necesidades y le daremos una cotización firme.',
    startingRates: 'Tarifas Iniciales',
    finalPricingDepends: 'El precio final depende de la complejidad. Estas son nuestras tarifas iniciales:',
    whyOurPricing: '¿Por Qué Nuestros Precios?',
    noHiddenFees: 'Sin cargos ocultos — cotización firme antes de comenzar',
    moreAffordable: 'Más asequible que firmas CPA',
    moreThorough: 'Más minucioso que software DIY',
    transparentInvoicing: 'Facturación transparente detallada',
    requestYourQuote: 'Solicite Su Cotización',
    tellUsNeeds: 'Cuéntenos sus necesidades y responderemos en 24 horas.',
    tellUsMore: 'Cuéntenos Más',
    describeYourSituation: 'Describa su situación fiscal, número de estados, tipo de negocio, circunstancias especiales...',
    requestQuote: 'Solicitar Cotización',
    quoteReceived: '¡Solicitud de Cotización Recibida!',
    prepareCustomQuote: 'Prepararemos una cotización personalizada y nos comunicaremos en 24 horas hábiles.',

    // Legal
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
    circular230Disclosure: 'Divulgación Circular 230 del IRS',
    effectiveDate: 'Fecha de Vigencia',
    requiredByIRS: 'Requerido por Regulaciones del IRS',
    governingLaw: 'Ley Aplicable: Estado de Georgia',

    // About
    meetFounder: 'Conozca a MaTaxPreparer LLC',
    ourFounder: 'Nuestro Fundador',
    irsLicensedPreparer: 'Preparador de Impuestos Licenciado por el IRS',
    ourValues: 'Nuestros Valores',
    whatWeStandFor: 'Lo Que Defendemos',
    accuracyFirst: 'Precisión Primero',
    accuracyFirstDesc: 'Cada declaración pasa por múltiples revisiones antes de presentar. Nunca nos apresuramos — lo hacemos bien.',
    confidentiality: 'Confidencialidad',
    confidentialityDesc: 'Su información financiera está encriptada y nunca se comparte sin su consentimiento explícito.',
    accessibility: 'Accesibilidad',
    accessibilityDesc: 'La experiencia fiscal premium no debería estar fuera de alcance. Mantenemos nuestros precios transparentes y justos.',
    yearRoundPartner: 'Socio Todo el Año',
    yearRoundPartnerDesc: 'La planificación fiscal es una conversación de 12 meses, no un evento anual.',
    findUs: 'Encuéntrenos',
    readyToWork: '¿Listo para Trabajar con Nosotros?',
    discoverMaximize: 'Reserve su consulta gratuita hoy y descubra cómo podemos maximizar su reembolso.',

    // Who We Help
    taxHelpEveryone: 'Ayuda Fiscal para Todos',
    fromSimpleW2: 'Desde declaraciones W-2 simples hasta declaraciones comerciales multi-estado complejas — tenemos la experiencia para manejar su situación fiscal única.',
    notSureWhereFit: '¿No Sabe Dónde Encaja?',
    assessYourSituation: 'Reserve una consulta gratuita y evaluaremos su situación y recomendaremos el mejor enfoque.',
    individualsAndFamilies: 'Individuos y Familias',
    w2Employees: 'Empleados W-2, jubilados, estudiantes',
    selfEmployedFreelancers: 'Autónomos y Freelancers',
    contractors1099: 'Contratistas 1099, trabajadores gig, consultores',
    smallMediumBusinesses: 'Pequeñas y Medianas Empresas',
    llcScorpCcorp: 'LLCs, S-Corps, C-Corps, sociedades',
    multiStateFilers: 'Declarantes Multi-Estado',
    remoteWorkers: 'Trabajadores remotos, viajeros, residentes duales',
    startupsNewBusinesses: 'Startups y Nuevos Negocios',
    earlyStageCompanies: 'Empresas en etapa inicial, fundadores',

    // How It Works
    theProcess: 'El Proceso',
    simpleTransparent: 'Simple, transparente y sin estrés. Desde la entrega de documentos hasta la declaración presentada en cuatro pasos fáciles.',
    submitYourDocuments: 'Envíe Sus Documentos',
    secureUploadMinutes: 'Carga segura en minutos',
    createFreeAccount: 'Cree su cuenta segura gratuita',
    uploadViEncrypted: 'Suba documentos a través del portal encriptado',
    followGuidedChecklist: 'Siga nuestra lista de documentos guiada',
    addNotesQuestions: 'Agregue notas o preguntas para su asesor',
    realTimeConfirmation: 'Confirmación de carga en tiempo real',
    expertReviewPrep: 'Revisión y Preparación Experta',
    dedicatedProfessional: 'Su profesional de impuestos dedicado trabajando',
    dedicatedAssigned: 'Profesional de impuestos dedicado asignado',
    lineByLineReview: 'Revisión línea por línea de documentos',
    maxDeductionsIdentified: 'Deducciones máximas identificadas',
    taxPlanningRecommendations: 'Recomendaciones de planificación fiscal',
    questionsAddressed: 'Preguntas abordadas proactivamente',
    reviewApprove: 'Revisar y Aprobar',
    alwaysInControl: 'Siempre tiene el control',
    clearExplanation: 'Explicación clara de su declaración',
    lineByLineWalkthrough: 'Recorrido línea por línea disponible',
    allQuestionsAnswered: 'Todas las preguntas respondidas',
    digitalSignature: 'Proceso de firma digital',
    approvalConvenience: 'Aprobación a su conveniencia',
    fileMaximize: 'Presentar y Maximizar',
    secureEfiling: 'E-filing seguro y seguimiento de reembolso',
    irsAuthorizedEfile: 'E-filing autorizado por el IRS',
    stateFilingAll50: 'Presentación estatal en los 50 estados',
    instantConfirmation: 'Confirmación instantánea de presentación',
    refundTracking: 'Seguimiento del estado de reembolso',
    postFilingSupport: 'Soporte post-presentación',
    fastTurnaround: 'Entrega Rápida',
    businessDays35: '3-5 días hábiles',
    beforeEveryFiling: 'Antes de cada presentación',
    federalState: 'Federal + Estatal',
    readyGetStarted: '¿Listo para Comenzar?',
    walkThroughProcess: 'Reserve su consulta gratuita y le guiaremos a través de todo el proceso.',

    // Blog
    taxInsights: 'Ideas Fiscales',
    theBlog: 'El Blog de MaTaxPreparer',
    expertInsights: 'Ideas expertas sobre impuestos, deducciones y estrategia financiera — escritas por nuestros profesionales fiscales.',
    searchArticles: 'Buscar artículos...',
    loadingArticles: 'Cargando artículos...',
    noArticlesFound: 'No se encontraron artículos.',
    backToBlog: 'Volver al Blog',
    haveTaxQuestions: '¿Tiene preguntas fiscales?',
    teamHereToHelp: 'Nuestro equipo en MaTaxPreparer LLC está aquí para ayudar. Reserve una consulta gratuita hoy.',

    // AI Chatbot
    aiChatbotName: 'Alex — Asesor Fiscal IA',
    aiChatbotGreeting: '¡Hola! Soy Alex, su asesor fiscal de IA de MaTaxPreparer LLC. ¿Cómo puedo ayudarle hoy? Puede preguntarme sobre deducciones fiscales, fechas límite, impuestos comerciales y más.',
    aiChatbotError: 'Tengo problemas para conectar ahora mismo. Por favor llámenos al 770-235-6999 o envíe un correo a mataxpreparerllc@yahoo.com para ayuda inmediata.',
    quickQuestions: 'Preguntas Rápidas',
    whatDocumentsNeed: '¿Qué documentos necesito para mis impuestos?',
    canDeductHomeOffice: '¿Puedo deducir gastos de oficina en casa?',
    whenFilingDeadline: '¿Cuándo es la fecha límite para declarar?',
    typeYourQuestion: 'Escriba su pregunta fiscal...',

    // Errors
    invalidEmail: 'Por favor ingrese un correo electrónico válido.',
    invalidPassword: 'La contraseña debe tener al menos 8 caracteres.',
    invalidPhone: 'Por favor ingrese un número de teléfono válido.',
    fieldRequired: 'Este campo es requerido.',
    somethingWentWrong: 'Algo salió mal. Por favor intente de nuevo.',
    emailAlreadyRegistered: 'Este correo ya está registrado.',
    invalidCredentials: 'Correo o contraseña inválidos.',
    sessionExpired: 'Su sesión ha expirado. Por favor inicie sesión de nuevo.',
    networkError: 'Error de red. Por favor verifique su conexión.',
    fileTooBig: 'El archivo debe ser menor a 5MB.',
    unsupportedFileType: 'Este tipo de archivo no está soportado.',
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // Check localStorage for saved preference, default to 'en'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('matax_lang') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('matax_lang', lang);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => {
    // Support nested keys like 'documentCategories.w2'
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value?.[k];
    }
    // Fallback to English if not found
    if (value === undefined) {
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }
    return value ?? key;
  };

  const toggleLang = () => setLang(l => l === 'en' ? 'es' : 'en');

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, toggleLang, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
